import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Megaphone } from "lucide-react";
import api from "@/lib/api";
import { FadeIn, Reveal } from "@/components/Reveal";

const VARIANTS = [
  { key: "default", label: "Paid ad", desc: "The default single-CTA page" },
  { key: "referral", label: "Referral push", desc: "For a 'referred by a friend' angle" },
  { key: "career", label: "Career-changer", desc: "Aimed at people switching track" },
];

export default function LandingIndex() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data.courses)).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-index-page">
      <section className="mesh-wash relative overflow-hidden">
        <div className="blob-decoration w-[420px] h-[420px] bg-lemonlight -top-32 -right-24" />
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12 pt-10 md:pt-14 pb-10 md:pb-14 relative">
          <FadeIn>
            <span className="inline-flex items-center gap-2 rounded-full bg-emeraldlight px-4 py-2 font-sans text-xs font-bold text-emerald mb-6">
              <Megaphone size={14} /> Marketing landing pages
            </span>
          </FadeIn>
          <h1 className="font-display font-bold tracking-tight text-4xl md:text-6xl leading-[1.02] max-w-4xl text-ink">
            Focused, single-CTA pages — one for every ad or push.
          </h1>
          <p className="font-sans text-lg md:text-xl text-graphite mt-6 max-w-2xl leading-relaxed">
            Each of these is a standalone page built around one course and one clear
            "Enquire" call-to-action — no navigation, minimal distraction. A single course
            can run several at once (a paid ad, a referral push, a career angle). Spinning
            up a new one is just a new link.
          </p>
        </div>
      </section>

      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <div className="space-y-4">
          {courses.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 4) * 0.05}>
              <div className="rounded-2xl bg-surface border border-line shadow-sm overflow-hidden flex flex-col md:flex-row">
                <div className="md:w-56 aspect-[16/10] md:aspect-auto overflow-hidden shrink-0">
                  <img src={c.image} alt={c.title} className="h-full w-full object-cover" />
                </div>
                <div className="p-6 flex-1">
                  <p className="font-sans text-xs font-bold text-terracotta uppercase tracking-wide">{c.category}</p>
                  <h3 className="font-display font-semibold text-2xl mt-1">{c.title}</h3>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {VARIANTS.map((v) => (
                      <Link
                        key={v.key}
                        to={v.key === "default" ? `/lp/${c.slug}` : `/lp/${c.slug}?v=${v.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`lp-link-${c.slug}-${v.key}`}
                        className="group rounded-xl border border-line hover:border-emerald p-3 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-sans font-bold text-emerald">{v.label}</span>
                          <ArrowUpRight size={16} className="text-graphite group-hover:text-emerald transition-colors" />
                        </div>
                        <p className="text-[13px] text-graphite mt-1 leading-snug">{v.desc}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
